import { useEffect, useState, Fragment, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { Card } from "reactstrap";
import ClientRow from "./ClientRow";
import ClientHeader from "./ClientHeader";
import Pagination from "../../../components/utils/Pagination";
import Loading from "../../../components/utils/Loading";
import { useTranslation } from "react-i18next";
import { getClientsList } from "../../../services/client";
import EmptyState from "../../../components/EmptyState";
import { toast } from "react-toastify";
import StandardTable from "../../../ui/components/StandardTable";

const Clients = ({
  token,
  onCreateClient,
  onOpenClient,
  onImportClients,
  refreshKey = 0,
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [allClients, setAllClients] = useState([]);
  const [keywordFilter, setKeywordFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [recordPerPage, setRecordPerPage] = useState(10);
  const [resultPage, setResultPage] = useState(0);
  const [totalEntry, setTotalEntry] = useState(0);

  const [sortByField, setSortByField] = useState("name");
  const [orderSortByField, setOrderSortByField] = useState("1");

  const hasAnyClients = useMemo(() => allClients.length > 0, [allClients]);
  const hasActiveFilters = Boolean(keywordFilter?.trim());

  const sortClients = (list, sortBy = "name", order = "1") => {
    const safeSortBy = sortBy || "name";
    return [...list].sort((a, b) => {
      let rawA = a?.[safeSortBy] ?? "";
      let rawB = b?.[safeSortBy] ?? "";
      if (safeSortBy === "createdAt") {
        rawA = rawA ? new Date(rawA).getTime() : 0;
        rawB = rawB ? new Date(rawB).getTime() : 0;
        if (rawA < rawB) return order === "1" ? -1 : 1;
        if (rawA > rawB) return order === "1" ? 1 : -1;
        return 0;
      }
      const aValue = String(rawA).toLowerCase();
      const bValue = String(rawB).toLowerCase();
      if (aValue < bValue) return order === "1" ? -1 : 1;
      if (aValue > bValue) return order === "1" ? 1 : -1;
      return 0;
    });
  };

  const filterClients = (list, keyword) => {
    if (!keyword || keyword.trim() === "") {
      return list;
    }
    const lowerKeyword = keyword.toLowerCase().trim();
    return list.filter((client) => {
      const name = String(client?.name || "").toLowerCase();
      const clientUniqueId = String(client?.clientUniqueId || "").toLowerCase();
      return (
        name.includes(lowerKeyword) || clientUniqueId.includes(lowerKeyword)
      );
    });
  };

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getClientsList();
      const clients = res.data.data || [];
      setAllClients(clients);
    } catch (error) {
      console.error(error);
      toast.error(t("settingsClients.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (token) {
      fetchClients();
    }
  }, [token, location.pathname, refreshKey, fetchClients]);

  const processedList = useMemo(() => {
    const filtered = filterClients(allClients, keywordFilter);
    return sortClients(filtered, sortByField, orderSortByField);
  }, [allClients, keywordFilter, sortByField, orderSortByField]);

  useEffect(() => {
    setTotalEntry(processedList.length);
    setCurrentPage(1);
  }, [processedList]);

  useEffect(() => {
    setCurrentPage(1);
  }, [recordPerPage]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * recordPerPage;
    return processedList.slice(startIndex, startIndex + recordPerPage);
  }, [processedList, currentPage, recordPerPage]);

  const hasClientsOnPage = paginatedData.length > 0;
  const showEmptyState = !hasAnyClients && !hasActiveFilters;

  if (!token) return null;
  if (loading) return <Loading color="#F5F5F5" />;

  return (
    <div className="w-full">
      <Card className="table border-0 shadow-none rounded-lg">
        <StandardTable
          fluidColCount={showEmptyState ? 1 : 3}
          fixedColWidth={0}
          containerClassName={`settings-clients-table${showEmptyState ? " table-standard-container--list-empty" : ""}`}
          columnAlignments={["left", "left", "left"]}
          topBar={
            hasAnyClients ? (
              <section className="table-standard-top-bar">
                <div className="table-standard-top-bar-inner">
                  <div className="flex gap-2 items-center flex-wrap flex-1 min-w-0">
                    <div className="search-input-wrapper">
                      <i className="fas fa-search search-input-icon" />
                      <input
                        type="text"
                        className="search-input"
                        value={keywordFilter}
                        placeholder={t("settingsClients.searchPlaceholder")}
                        onChange={(event) =>
                          setKeywordFilter(event.target.value)
                        }
                      />
                    </div>
                  </div>
                  <div className="create-button-wrapper gap-2">
                    <button
                      className="create-button create-button--secondary"
                      type="button"
                      onClick={onImportClients}
                    >
                      {t("settingsClients.importButton")}
                    </button>
                    <button
                      className="create-button"
                      type="button"
                      onClick={onCreateClient}
                    >
                      {t("settingsClients.createNew")}
                    </button>
                  </div>
                </div>
              </section>
            ) : null
          }
          header={
            hasAnyClients ? (
              <ClientHeader
                onSort={(field, order) => {
                  setSortByField(field);
                  setOrderSortByField(order);
                }}
                sortByField={sortByField}
                orderSortByField={orderSortByField}
              />
            ) : null
          }
          bottom={
            hasAnyClients ? (
              <div className="table-standard-bottom table-pagination-bottom">
                <Pagination
                  totalEntry={totalEntry}
                  recordPerPage={recordPerPage}
                  setRecordPerPage={setRecordPerPage}
                  currentPage={currentPage}
                  setCurrentPage={setCurrentPage}
                  resultPage={resultPage}
                  setResultPage={setResultPage}
                />
              </div>
            ) : null
          }
        >
          {hasClientsOnPage ? (
            paginatedData.map((client) => (
              <Fragment key={client._id}>
                <ClientRow data={client} onOpen={onOpenClient} />
              </Fragment>
            ))
          ) : hasAnyClients && hasActiveFilters ? (
            <div className="table-standard-row table-standard-row--empty">
              <div
                className="table-standard-row-cell"
                style={{ gridColumn: "span 3" }}
              >
                {t("No results match your search")}
              </div>
            </div>
          ) : (
            <EmptyState
              title={t("settingsClients.emptyTitle")}
              description={t("settingsClients.emptyHint")}
              buttonText={t("settingsClients.createNew")}
              onButtonClick={onCreateClient}
              buttonIcon={null}
              secondaryButtonText={t("settingsClients.importButton")}
              onSecondaryButtonClick={onImportClients}
            />
          )}
        </StandardTable>
      </Card>
    </div>
  );
};

export default Clients;
